import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { companyFormToast, companyFormToastError } from "./companyFormSwal";

export default function CompanyFormsAddCategoryModal({ open, onClose, onCreate }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setLabel("");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    if (busy) return;
    setLabel("");
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = label.trim();
    if (!onCreate || !trimmed) return;

    setBusy(true);
    try {
      const created = await onCreate(trimmed);
      companyFormToast({
        title: "Category added",
        text: `"${created?.label || trimmed}" is available in the list.`,
      });
      setLabel("");
      onClose();
    } catch (error) {
      companyFormToastError(
        "Could not add category",
        error.response?.data?.message || error.message || "Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-slate-900/45"
        onMouseDown={handleClose}
        aria-hidden="true"
      />
      <div className="cms-app-modal cms-app-modal--centered pointer-events-none flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-category-title"
          className="pointer-events-auto w-[min(calc(100vw-1.5rem),36rem)] overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="tailwind-container company-forms-module box-border w-full max-w-none text-[#1a1f36]">
            <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3.5">
              <h2 id="add-category-title" className="m-0 text-base font-semibold text-slate-900">
                Add category
              </h2>
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                aria-label="Close"
                className="company-forms-btn inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              >
                <FaTimes size={12} aria-hidden="true" />
              </button>
            </header>

            <div className="w-full px-4 py-4">
              <label
                htmlFor="new-category-name"
                className="mb-1.5 block text-xs font-semibold text-slate-700"
              >
                Category name
              </label>
              <input
                id="new-category-name"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="e.g. Privacy Policy"
                maxLength={120}
                autoFocus
                className="box-border block w-full max-w-none rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-[#007BFF]/55 focus:ring-2 focus:ring-[#007BFF]/15"
                onKeyDown={(event) => {
                  if (event.key === "Enter" && label.trim() && !busy) {
                    event.preventDefault();
                    handleSubmit();
                  }
                }}
              />
            </div>

            <footer className="flex justify-end gap-2.5 border-t border-slate-100 bg-slate-50 px-4 py-3.5">
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="company-forms-btn rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || !label.trim()}
                onClick={handleSubmit}
                className="company-forms-btn rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0069d9] disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </footer>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
